// src/components/intentions/PreferencesSection.tsx
import React, { useState, useEffect } from 'react';
import { GripVertical, X, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { PreferencesData } from '../../types/intentionPeriods';
import { supabase } from '../../supabase/config';
import { getOfferedSubjects } from '../../services/offeredSubjectsService';

interface PreferencesSectionProps {
  preferences: PreferencesData;
  onChange: (preferences: PreferencesData) => void;
  academicYear: number;
  disabled?: boolean;
}

interface CurriculumArea {
  id: string;
  code: string;
  name: string;
}

const YEAR_LEVELS = [
  { id: 'year7', label: 'Year 7' },
  { id: 'year8', label: 'Year 8' },
  { id: 'year9', label: 'Year 9' },
  { id: 'year10', label: 'Year 10' },
  { id: 'year11', label: 'Year 11' },
  { id: 'year12', label: 'Year 12' }
];

const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  preferences,
  onChange,
  academicYear,
  disabled = false
}) => {
  const [curriculumAreas, setCurriculumAreas] = useState<CurriculumArea[]>([]);
  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('');

  // Fetch curriculum areas (reference table) and the offered-subjects list
  // (sourced live from the timetabling grid — the authoritative "subjects we
  // offer" — deduped so each subject appears once regardless of how many
  // classes of it run).
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch curriculum areas
        const { data: areas, error: areasError } = await supabase
          .from('curriculum_areas')
          .select('id, code, name')
          .eq('is_active', true)
          .order('display_order')
          .order('name');

        if (areasError) throw areasError;
        setCurriculumAreas(areas || []);

        // Subjects come from the grid for this period's academic year.
        const offered = await getOfferedSubjects(academicYear);
        setSubjects(offered);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [academicYear]);

  // Handle curriculum area ranking
  const addCurriculumArea = (area: CurriculumArea) => {
    const currentAreas = preferences.curriculumAreas || [];
    if (currentAreas.some(a => a.areaId === area.id)) return;

    const newArea = {
      areaId: area.id,
      areaName: area.name,
      rank: currentAreas.length + 1
    };

    onChange({
      ...preferences,
      curriculumAreas: [...currentAreas, newArea]
    });
  };

  const removeCurriculumArea = (areaId: string) => {
    const currentAreas = preferences.curriculumAreas || [];
    const filtered = currentAreas.filter(a => a.areaId !== areaId);

    // Re-rank remaining areas
    const reranked = filtered.map((a, index) => ({
      ...a,
      rank: index + 1
    }));

    onChange({
      ...preferences,
      curriculumAreas: reranked
    });
  };

  const moveAreaUp = (index: number) => {
    if (index === 0) return;
    const currentAreas = [...(preferences.curriculumAreas || [])];
    [currentAreas[index - 1], currentAreas[index]] = [currentAreas[index], currentAreas[index - 1]];

    // Re-rank
    const reranked = currentAreas.map((a, i) => ({ ...a, rank: i + 1 }));
    onChange({ ...preferences, curriculumAreas: reranked });
  };

  const moveAreaDown = (index: number) => {
    const currentAreas = preferences.curriculumAreas || [];
    if (index >= currentAreas.length - 1) return;

    const newAreas = [...currentAreas];
    [newAreas[index], newAreas[index + 1]] = [newAreas[index + 1], newAreas[index]];

    // Re-rank
    const reranked = newAreas.map((a, i) => ({ ...a, rank: i + 1 }));
    onChange({ ...preferences, curriculumAreas: reranked });
  };

  // Handle subject selection
  const addSubject = (subject: { id: string; name: string }) => {
    const currentSubjects = preferences.subjects || [];
    if (currentSubjects.some(s => s.subjectId === subject.id)) return;

    onChange({
      ...preferences,
      subjects: [
        ...currentSubjects,
        { subjectId: subject.id, subjectName: subject.name, rank: currentSubjects.length + 1 }
      ]
    });
  };

  const removeSubject = (subjectId: string) => {
    const currentSubjects = preferences.subjects || [];
    const filtered = currentSubjects.filter(s => s.subjectId !== subjectId);
    const reranked = filtered.map((s, i) => ({ ...s, rank: i + 1 }));
    onChange({
      ...preferences,
      subjects: reranked
    });
  };

  const moveSubjectUp = (index: number) => {
    if (index === 0) return;
    const current = [...(preferences.subjects || [])];
    [current[index - 1], current[index]] = [current[index], current[index - 1]];
    const reranked = current.map((s, i) => ({ ...s, rank: i + 1 }));
    onChange({ ...preferences, subjects: reranked });
  };

  const moveSubjectDown = (index: number) => {
    const current = preferences.subjects || [];
    if (index >= current.length - 1) return;
    const next = [...current];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    const reranked = next.map((s, i) => ({ ...s, rank: i + 1 }));
    onChange({ ...preferences, subjects: reranked });
  };

  // Handle year level selection
  const toggleYearLevel = (yearId: string) => {
    const currentLevels = preferences.yearLevels || [];
    const newLevels = currentLevels.includes(yearId)
      ? currentLevels.filter(y => y !== yearId)
      : [...currentLevels, yearId];

    onChange({
      ...preferences,
      yearLevels: newLevels
    });
  };

  const selectedAreaIds = (preferences.curriculumAreas || []).map(a => a.areaId);
  const availableAreas = curriculumAreas.filter(a => !selectedAreaIds.includes(a.id));

  const selectedSubjectIds = (preferences.subjects || []).map(s => s.subjectId);
  const filteredSubjects = subjects.filter(s =>
    !selectedSubjectIds.includes(s.id) &&
    s.name.toLowerCase().includes(subjectSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-ink-200 p-6">
        <h3 className="text-lg font-semibold text-ink-900 mb-4">Teaching Preferences</h3>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-ink-200 rounded w-1/3"></div>
          <div className="h-32 bg-ink-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-ink-200 p-6">
      <h3 className="text-lg font-semibold text-ink-900 mb-4">Teaching Preferences</h3>

      {/* Explainer */}
      <div className="mb-5 p-3 bg-navy-50 border border-navy-200 rounded-md text-sm text-navy-900">
        <p>
          <strong>How preferences are used:</strong> Select the learning area(s) you'd like to teach. If you <em>only</em> select a learning area, that signals you don't mind which specific subjects you're allocated within that area — you may be assigned any subject within it. If you want a say in which subjects, you must also nominate specific subjects in the section below.
        </p>
      </div>

      {/* Curriculum Areas (Ranked) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Learning Areas (Ranked by Preference) <span className="text-danger-fg">*</span>
        </label>
        <p className="text-xs text-ink-500 mb-3">
          Add and rank your preferred learning areas. Drag or use arrows to reorder.
        </p>

        {/* Selected areas list */}
        <div className="space-y-2 mb-3">
          {(preferences.curriculumAreas || []).map((area, index) => (
            <div
              key={area.areaId}
              className="flex items-center gap-2 p-3 bg-ink-50 rounded-lg border border-ink-200"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-navy-600 text-white rounded-full text-xs font-bold font-mono tabular-nums">
                {area.rank}
              </span>
              <GripVertical className="w-4 h-4 text-ink-400" />
              <span className="flex-1 font-medium text-ink-900">{area.areaName}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveAreaUp(index)}
                  disabled={disabled || index === 0}
                  className="p-1 text-ink-400 hover:text-ink-500 disabled:opacity-30"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveAreaDown(index)}
                  disabled={disabled || index === (preferences.curriculumAreas || []).length - 1}
                  className="p-1 text-ink-400 hover:text-ink-500 disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeCurriculumArea(area.areaId)}
                  disabled={disabled}
                  className="p-1 text-danger-solid hover:text-danger-fg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add area dropdown */}
        {availableAreas.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableAreas.map((area) => (
              <button
                key={area.id}
                type="button"
                onClick={() => addCurriculumArea(area)}
                disabled={disabled}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-ink-300 rounded-full hover:bg-ink-50 transition-colors"
              >
                <Plus className="w-3 h-3" />
                {area.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Specific Subjects (Ranked) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Specific Subjects (Ranked by Preference)
        </label>
        <p className="text-xs text-ink-500 mb-3">
          Add and rank specific subjects you would like to teach. Use arrows to reorder.
        </p>

        {/* Selected subjects (ranked list) */}
        <div className="space-y-2 mb-3">
          {(preferences.subjects || []).map((subject, index) => (
            <div
              key={subject.subjectId}
              className="flex items-center gap-2 p-3 bg-ink-50 rounded-lg border border-ink-200"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-navy-600 text-white rounded-full text-xs font-bold font-mono tabular-nums">
                {subject.rank ?? index + 1}
              </span>
              <GripVertical className="w-4 h-4 text-ink-400" />
              <span className="flex-1 font-medium text-ink-900">{subject.subjectName}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveSubjectUp(index)}
                  disabled={disabled || index === 0}
                  className="p-1 text-ink-400 hover:text-ink-500 disabled:opacity-30"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveSubjectDown(index)}
                  disabled={disabled || index === (preferences.subjects || []).length - 1}
                  className="p-1 text-ink-400 hover:text-ink-500 disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeSubject(subject.subjectId)}
                  disabled={disabled}
                  className="p-1 text-danger-solid hover:text-danger-fg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Subject search */}
        {subjects.length === 0 ? (
          <p className="text-xs text-ink-500 italic">
            No subjects available yet — ask a curriculum leader to set up the grid for this year.
          </p>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={subjectSearchTerm}
              onChange={(e) => setSubjectSearchTerm(e.target.value)}
              disabled={disabled}
              placeholder="Search subjects to add..."
              className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
            />
            {subjectSearchTerm && filteredSubjects.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-ink-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredSubjects.slice(0, 10).map((subject) => (
                  <button
                    key={subject.id}
                    type="button"
                    onClick={() => {
                      addSubject(subject);
                      setSubjectSearchTerm('');
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-ink-50"
                  >
                    {subject.name}
                  </button>
                ))}
              </div>
            )}
            {subjectSearchTerm && filteredSubjects.length === 0 && (
              <p className="mt-2 text-xs text-ink-500 italic">
                No subjects match "{subjectSearchTerm}".
              </p>
            )}
          </div>
        )}
      </div>

      {/* Year Levels */}
      <div>
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Preferred Year Levels <span className="text-danger-fg">*</span>
        </label>
        <p className="text-xs text-ink-500 mb-3">
          Select at least one year level you prefer to teach
        </p>
        <div className="flex flex-wrap gap-2">
          {YEAR_LEVELS.map((level) => (
            <button
              key={level.id}
              type="button"
              onClick={() => toggleYearLevel(level.id)}
              disabled={disabled}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                (preferences.yearLevels || []).includes(level.id)
                  ? 'bg-navy-600 text-white border-navy-600'
                  : 'bg-white text-ink-700 border-ink-300 hover:bg-ink-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {level.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;
